import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { useModal } from '../context/ModalContext';
import { useAuth } from '../context/AuthContext';

// ── Constants ──────────────────────────────────────────────────────────────────
const MONO    = "'JetBrains Mono', monospace";
const SERIF   = "'DM Serif Display', serif";
const HAND    = "'Caveat', cursive";
const CREAM   = '#FFF8F0';
const CHARCOAL     = '#2C2417';
const CHARCOAL_MID = '#5C4A35';
const CHARCOAL_MUT = '#9C8872';
const ORANGE  = '#ff780a';
const RULE    = 'rgba(44,36,23,0.11)';

// ── Animation presets ──────────────────────────────────────────────────────────
const fadeUp = {
  hidden:  { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.85, ease: [0.16, 1, 0.3, 1] } },
};
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.12 } } };

// ── Shared helpers ─────────────────────────────────────────────────────────────
const ELabel = ({ children, light = false }) => (
  <div className="flex items-center gap-3 mb-7">
    <div className="w-5 h-px flex-shrink-0" style={{ background: 'rgba(255,120,10,0.55)' }} />
    <span style={{ fontFamily: MONO, fontSize: '10px', letterSpacing: '0.17em', textTransform: 'uppercase', color: ORANGE }}>
      {children}
    </span>
  </div>
);

const Rule = () => (
  <div className="max-w-[1200px] mx-auto px-6 md:px-12">
    <div className="w-full h-px" style={{ background: RULE }} />
  </div>
);

const useReveal = (opts = {}) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px', ...opts });
  return { ref, inView };
};

// ── Wavelength Demo ──────────────────────────────────────────────────────────
const WavelengthDemo = () => {
  const D = 5;
  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <div className="grid grid-cols-2 gap-3.5">
        {[
          { tag: 'You · 1/2', delay: 0 },
          { tag: 'Partner · 2/2 ●', highlight: true, delay: 0.55 },
        ].map((card, i) => (
          <div
            key={i}
            className="rounded-[20px] p-6 flex flex-col"
            style={{
              background: card.highlight ? 'rgba(255,255,255,0.09)' : 'rgba(255,255,255,0.04)',
              border: `1px solid ${card.highlight ? 'rgba(255,120,10,0.32)' : 'rgba(255,248,240,0.08)'}`,
              backdropFilter: 'blur(12px)',
            }}
          >
            <div style={{ fontFamily: MONO, fontSize: '10px', letterSpacing: '0.12em', textTransform: 'uppercase', color: ORANGE, marginBottom: '16px' }}>
              {card.tag}
            </div>
            <p style={{ fontFamily: SERIF, fontSize: '15.5px', lineHeight: 1.45, color: CREAM, marginBottom: '22px', letterSpacing: '-0.02em', flex: 1 }}>
              "What's something about the way you've been showing up lately that you quietly like?"
            </p>
            <motion.button
              className="w-full py-2.5 rounded-full text-xs font-medium mb-2 text-white"
              style={{ outline: 'none', border: 'none', cursor: 'default' }}
              animate={{
                backgroundColor: ['rgba(255,120,10,0.22)', 'rgba(255,120,10,0.22)', '#ff780a', '#ff780a', 'rgba(255,120,10,0.22)'],
                boxShadow: ['none', 'none', '0 0 14px rgba(255,120,10,0.35)', '0 0 14px rgba(255,120,10,0.35)', 'none'],
              }}
              transition={{ duration: D, repeat: Infinity, ease: 'easeInOut', times: [0, 0.48, 0.62, 0.84, 1], delay: card.delay }}
            >
              I&apos;m ready for this
            </motion.button>
            <div className="w-full py-2.5 rounded-full text-xs text-center" style={{ background: 'rgba(255,248,240,0.07)', color: 'rgba(255,248,240,0.38)' }}>
              Not this one
            </div>
          </div>
        ))}
      </div>

      {/* "Both ready" — appears when both buttons are active; contained, no bleed */}
      <motion.div
        className="flex items-center justify-center gap-3 mt-3"
        animate={{ opacity: [0, 0, 0, 1, 1, 0], y: [6, 6, 6, 0, 0, 6] }}
        transition={{ duration: D, repeat: Infinity, ease: 'easeInOut', times: [0, 0.5, 0.66, 0.74, 0.86, 0.97] }}
      >
        <div style={{ flex: 1, height: '1px', background: 'rgba(255,120,10,0.3)' }} />
        <span style={{ fontFamily: HAND, fontSize: '17px', color: 'rgba(255,248,240,0.65)', whiteSpace: 'nowrap' }}>both ready</span>
        <div style={{ flex: 1, height: '1px', background: 'rgba(255,120,10,0.3)' }} />
      </motion.div>
    </div>
  );
};

// ── 1. Transition / Hero Statement ─────────────────────────────────────────────
const TransitionSection = () => {
  const { ref, inView } = useReveal();
  return (
    <section
      ref={ref}
      className="relative py-36 px-6 overflow-hidden"
      style={{ background: `radial-gradient(ellipse 80% 40% at 50% 0%, rgba(255,120,10,0.08) 0%, transparent 65%), ${CREAM}` }}
    >
      <motion.div
        variants={stagger}
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
        className="max-w-[800px] mx-auto text-center"
      >
        <motion.span
          variants={fadeUp}
          style={{ fontFamily: HAND, fontSize: '19px', color: CHARCOAL_MUT, display: 'block', marginBottom: '28px' }}
        >
          for the conversations that matter
        </motion.span>

        <motion.h1
          variants={fadeUp}
          style={{
            fontFamily: SERIF,
            fontSize: 'clamp(52px, 7vw, 96px)',
            lineHeight: 1.04,
            letterSpacing: '-0.045em',
            color: CHARCOAL,
            marginBottom: '36px',
          }}
        >
          Some conversations
          <br />
          <em style={{ fontStyle: 'italic', color: ORANGE }}>change things.</em>
        </motion.h1>

        <motion.p
          variants={fadeUp}
          style={{ fontSize: '19px', fontWeight: 300, color: CHARCOAL_MID, maxWidth: '48ch', margin: '0 auto', lineHeight: 1.75 }}
        >
          Remote Conversations gives you one thought to open with — before the agenda starts,
          before the usual, before it goes where it always goes.
        </motion.p>
      </motion.div>
    </section>
  );
};

// ── 2. The Problem ─────────────────────────────────────────────────────────────
const WhySection = () => {
  const { ref, inView } = useReveal();
  return (
    <section ref={ref} className="py-28 px-6 md:px-12" style={{ background: CREAM }}>
      <motion.div
        variants={stagger}
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
        className="max-w-[1200px] mx-auto"
      >
        <div className="grid md:grid-cols-2 gap-20 items-start">
          {/* Sticky headline */}
          <motion.div variants={fadeUp} className="md:sticky md:top-24">
            <ELabel>The problem</ELabel>
            <h2
              style={{
                fontFamily: SERIF,
                fontSize: 'clamp(42px, 5vw, 68px)',
                lineHeight: 1.08,
                letterSpacing: '-0.04em',
                color: CHARCOAL,
              }}
            >
              Remote work quietly
              <br />
              took something
              <br />
              <em style={{ fontStyle: 'italic', color: ORANGE }}>from us.</em>
            </h2>
          </motion.div>

          {/* Body */}
          <motion.div variants={stagger} className="pt-1">
            <motion.p variants={fadeUp} style={{ fontSize: '17.5px', fontWeight: 300, lineHeight: 1.8, color: CHARCOAL_MID, marginBottom: '24px' }}>
              Not the work itself. Teams are often more productive than ever. What it took were the
              accidental moments in between — the walk to the boardroom, the coffee before everyone
              sits down, the two minutes of nothing in particular that somehow told you who you were
              working with.
            </motion.p>

            <motion.blockquote
              variants={fadeUp}
              style={{
                fontFamily: SERIF,
                fontSize: '22px',
                fontStyle: 'italic',
                lineHeight: 1.5,
                letterSpacing: '-0.02em',
                color: CHARCOAL,
                borderLeft: `2px solid ${ORANGE}`,
                padding: '16px 24px',
                margin: '32px 0',
                background: 'rgba(255,120,10,0.04)',
                borderRadius: '0 8px 8px 0',
              }}
            >
              "Teams that never connect eventually become teams that quietly disengage — and no one
              can point to exactly when it happened."
            </motion.blockquote>

            <motion.p variants={fadeUp} style={{ fontSize: '17.5px', fontWeight: 300, lineHeight: 1.8, color: CHARCOAL_MID }}>
              Remote Conversations gives those five minutes back, with intention. One thought. One
              question. The kind that would never have found its way into your conversation on its own.
            </motion.p>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

// ── 3. How It Works ────────────────────────────────────────────────────────────
const steps = [
  {
    num: '01 — Open',
    title: 'A thought is\nalready waiting.',
    body: 'Open remoteconversations.com on your phone or laptop. One thought fills the screen — nothing else competing for attention. No menus, no notifications, no friction. Just the question.',
    example: '"What\'s the most interesting tab you have open right now that isn\'t work?"',
    cta: false,
  },
  {
    num: '02 — Share',
    title: 'Read it out loud.\nOr share your screen.',
    body: 'Drop it in the meeting chat. Share your screen. Read it out loud. However it enters the room, it changes the room. Every day, one thought is shared across every visit — a quiet sense of community across time zones.',
    example: '"What\'s something you quietly changed your mind about recently?"',
    cta: false,
  },
  {
    num: '03 — Talk',
    title: 'Five minutes.\nThen start the meeting.',
    body: 'Press "We talked about this." That\'s it. The app earns you points, logs your streak, and moves on. Then the agenda begins — with people who are now actually present with each other.',
    example: null,
    cta: true,
  },
];

const HowItWorksSection = ({ handleCTA }) => {
  const { ref, inView } = useReveal();
  return (
    <section ref={ref} id="how-it-works" className="py-28 px-6 md:px-12" style={{ background: CREAM }}>
      <motion.div
        variants={stagger}
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
        className="max-w-[1200px] mx-auto"
      >
        {/* Header */}
        <div className="grid md:grid-cols-2 gap-16 items-end mb-6">
          <motion.div variants={fadeUp}>
            <ELabel>How it works</ELabel>
            <h2 style={{ fontFamily: SERIF, fontSize: 'clamp(36px, 4.4vw, 62px)', lineHeight: 1.1, letterSpacing: '-0.035em', color: CHARCOAL }}>
              Five minutes.
              <br />
              One thought.
              <br />
              <em style={{ fontStyle: 'italic', color: ORANGE }}>Before every meeting.</em>
            </h2>
          </motion.div>
          <motion.p variants={fadeUp} style={{ fontSize: '18px', fontWeight: 300, color: CHARCOAL_MID, lineHeight: 1.75, maxWidth: '50ch' }}>
            There is no onboarding, no tutorial, no setup. You open the app, a thought is already
            waiting. You read it out loud. You talk. Then the meeting begins — with people who are
            now actually present.
          </motion.p>
        </div>

        {/* Sub-header above grid */}
        <motion.p
          variants={fadeUp}
          className="mb-10"
          style={{ fontFamily: HAND, fontSize: '22px', color: CHARCOAL_MUT }}
        >
          3 simple steps. That's it.
        </motion.p>

        {/* Bordered grid */}
        <motion.div
          variants={stagger}
          className="grid md:grid-cols-3 gap-px rounded-[20px] overflow-hidden"
          style={{ background: RULE, border: `1px solid ${RULE}` }}
        >
          {steps.map((step) => (
            <motion.div
              key={step.num}
              variants={fadeUp}
              className="p-10 md:p-12 flex flex-col transition-colors duration-200"
              style={{ background: CREAM }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.7)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = CREAM; }}
            >
              <div style={{ fontFamily: MONO, fontSize: '10px', letterSpacing: '0.12em', color: ORANGE, marginBottom: '28px', opacity: 0.85 }}>
                {step.num}
              </div>
              <h3 style={{ fontFamily: SERIF, fontSize: '27px', letterSpacing: '-0.025em', color: CHARCOAL, marginBottom: '14px', lineHeight: 1.15, whiteSpace: 'pre-line' }}>
                {step.title}
              </h3>
              <p style={{ fontSize: '15px', fontWeight: 300, color: CHARCOAL_MID, lineHeight: 1.72, flex: 1 }}>
                {step.body}
              </p>
              {step.example && (
                <p style={{ fontFamily: SERIF, fontSize: '14.5px', fontStyle: 'italic', color: CHARCOAL_MUT, marginTop: '20px', padding: '14px 16px', borderLeft: `2px solid rgba(255,120,10,0.14)`, lineHeight: 1.55, borderRadius: '0 6px 6px 0' }}>
                  {step.example}
                </p>
              )}
              {step.cta && (
                <button
                  onClick={handleCTA}
                  className="mt-6 w-fit text-left transition-colors duration-200"
                  style={{ fontSize: '15px', fontWeight: 300, color: CHARCOAL_MID }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = CHARCOAL; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = CHARCOAL_MID; }}
                  data-testid="landing-cta-step3"
                >
                  Create a free account ↗
                </button>
              )}
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};

// ── 4. Depth Levels ────────────────────────────────────────────────────────────
const depthLevels = [
  {
    dots: 1, levelNum: '01', name: 'Spark', badge: 'Spark', badgeStyle: { background: 'rgba(255,120,10,0.1)', color: ORANGE },
    desc: 'Light, easy, low-stakes. Anyone can answer in under a minute. No vulnerability required. Great for first calls, Monday standups, or any time you need to get voices in the room.',
    example: '"What\'s something you\'ve been quietly enjoying lately?"',
  },
  {
    dots: 2, levelNum: '02', name: 'Depth', badge: 'Depth', badgeStyle: { background: 'rgba(44,36,23,0.08)', color: CHARCOAL_MID },
    desc: 'Requires genuine consideration. Reveals values, history, perspective. For groups with some existing trust — where people are ready to say something true, and be surprised by each other.',
    example: '"What\'s a belief you held five years ago you\'ve completely changed your mind on?"',
  },
  {
    dots: 3, levelNum: '03', name: 'Breakthrough', badge: 'Breakthrough', badgeStyle: { background: 'rgba(44,36,23,0.15)', color: CHARCOAL },
    desc: 'For teams with deep trust. These sit at the edges of vulnerability and meaning. Used at the right moment, they shift how a team sees itself permanently.',
    example: '"What part of yourself do you quietly leave at the door when you log on each morning?"',
  },
];

const DepthSection = () => {
  const { ref, inView } = useReveal();
  return (
    <section ref={ref} id="levels" className="py-28 px-6 md:px-12" style={{ background: `radial-gradient(ellipse 100% 60% at 50% 50%, rgba(255,120,10,0.04) 0%, transparent 70%), ${CREAM}` }}>
      <motion.div
        variants={stagger}
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
        className="max-w-[1200px] mx-auto"
      >
        {/* Header */}
        <div className="grid md:grid-cols-2 gap-16 items-end mb-16">
          <motion.div variants={fadeUp}>
            <ELabel>Depth levels</ELabel>
            <h2 style={{ fontFamily: SERIF, fontSize: 'clamp(36px, 4.4vw, 62px)', lineHeight: 1.1, letterSpacing: '-0.035em', color: CHARCOAL }}>
              Not every moment
              <br />
              calls for the same
              <br />
              <em style={{ fontStyle: 'italic', color: ORANGE }}>depth.</em>
            </h2>
          </motion.div>
          <motion.p variants={fadeUp} style={{ fontSize: '18px', fontWeight: 300, color: CHARCOAL_MID, lineHeight: 1.75, maxWidth: '50ch' }}>
            Start where your team is. Go as far as the trust allows. Every level has its place — from
            the Monday morning warmup to the offsite that actually changed something.
          </motion.p>
        </div>

        {/* Bordered grid */}
        <motion.div
          variants={stagger}
          className="grid md:grid-cols-3 gap-px rounded-[20px] overflow-hidden"
          style={{ background: RULE, border: `1px solid ${RULE}` }}
        >
          {depthLevels.map((d) => (
            <motion.div
              key={d.name}
              variants={fadeUp}
              className="p-10 md:p-12 flex flex-col transition-colors duration-200"
              style={{ background: CREAM }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.7)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = CREAM; }}
            >
              {/* Dots + level num */}
              <div className="flex items-center gap-2 mb-5">
                <div className="flex gap-1">
                  {Array.from({ length: 3 }).map((_, j) => (
                    <div key={j} className="w-[5px] h-[5px] rounded-full" style={{ background: j < d.dots ? ORANGE : 'rgba(44,36,23,0.2)' }} />
                  ))}
                </div>
                <span style={{ fontFamily: MONO, fontSize: '10px', letterSpacing: '0.14em', textTransform: 'uppercase', color: CHARCOAL_MUT }}>
                  Level {d.levelNum}
                </span>
              </div>
              <h3 style={{ fontFamily: SERIF, fontSize: '34px', letterSpacing: '-0.03em', color: CHARCOAL, marginBottom: '12px', lineHeight: 1.1 }}>
                {d.name}
              </h3>
              <p style={{ fontSize: '15px', fontWeight: 300, color: CHARCOAL_MID, lineHeight: 1.72, flex: 1, marginBottom: '24px' }}>
                {d.desc}
              </p>
              <p style={{ fontFamily: SERIF, fontSize: '14.5px', fontStyle: 'italic', color: CHARCOAL_MUT, padding: '14px 16px', borderLeft: `2px solid rgba(255,120,10,0.14)`, lineHeight: 1.55, borderRadius: '0 6px 6px 0', marginBottom: '20px' }}>
                {d.example}
              </p>
              <div>
                <span
                  style={{
                    display: 'inline-block',
                    padding: '5px 13px',
                    borderRadius: '100px',
                    fontSize: '10.5px',
                    fontWeight: 500,
                    letterSpacing: '0.07em',
                    textTransform: 'uppercase',
                    fontFamily: MONO,
                    ...d.badgeStyle,
                  }}
                >
                  {d.badge}
                </span>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};

// ── 5. Wavelength ──────────────────────────────────────────────────────────────
const WavelengthSection = () => {
  const { ref, inView } = useReveal();
  return (
    <section ref={ref} id="wavelength" className="relative overflow-hidden" style={{ background: CHARCOAL }}>
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 55% 45% at 50% 55%, rgba(255,120,10,0.1) 0%, transparent 100%)' }} />

      <motion.div
        variants={stagger}
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
        className="max-w-[1200px] mx-auto px-6 md:px-12 py-28 relative z-10"
      >
        <div className="grid md:grid-cols-2 gap-20 items-center">
          {/* Left */}
          <motion.div variants={stagger}>
            <motion.div variants={fadeUp}>
              <div className="flex items-center gap-3 mb-7">
                <div className="w-5 h-px" style={{ background: 'rgba(255,120,10,0.55)' }} />
                <span style={{ fontFamily: MONO, fontSize: '10px', letterSpacing: '0.17em', textTransform: 'uppercase', color: ORANGE }}>Wavelength sessions</span>
              </div>
            </motion.div>
            <motion.h2 variants={fadeUp} style={{ fontFamily: SERIF, fontSize: 'clamp(36px, 4.5vw, 56px)', lineHeight: 1.1, letterSpacing: '-0.035em', color: CREAM, marginBottom: '28px' }}>
              Both people.
              <br />
              Same thought.
              <br />
              Real time.
            </motion.h2>
            <motion.p variants={fadeUp} style={{ fontSize: '17px', fontWeight: 300, color: 'rgba(255,248,240,0.6)', lineHeight: 1.78, marginBottom: '18px', maxWidth: '46ch' }}>
              Share a unique link before your 1-on-1. Both people open it and see the same thought
              simultaneously. Both vote at the same moment.
            </motion.p>
            <motion.p variants={fadeUp} style={{ fontSize: '17px', fontWeight: 300, color: 'rgba(255,248,240,0.6)', lineHeight: 1.78, marginBottom: '32px', maxWidth: '46ch' }}>
              If one person says "Not this one," the thought dissolves quietly and a new one arrives.
              The no feels as comfortable as the yes. That's what makes people honest.
            </motion.p>
            <motion.div
              variants={fadeUp}
              className="inline-flex items-center gap-3"
              style={{ fontFamily: MONO, fontSize: '12.5px', color: 'rgba(255,248,240,0.38)', padding: '12px 20px', border: '1px solid rgba(255,248,240,0.1)', borderRadius: '100px' }}
            >
              <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
              remoteconversations.com/<strong style={{ color: ORANGE }}>gentle-breeze-87</strong>
            </motion.div>
          </motion.div>

          {/* Right — interactive demo */}
          <motion.div variants={fadeUp} className="flex items-center justify-center">
            <WavelengthDemo />
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

// ── 6. Who It's For ────────────────────────────────────────────────────────────
const allTypes = [
  { symbol: '○', name: 'General',      cat: 'Universal',               comingSoon: false, desc: "Works for any context, any relationship, any moment. The default for when you're not sure where your team is yet." },
  { symbol: '· ·', name: 'Colleague',  cat: '1-1 Working Relationships', comingSoon: false, desc: "Manager check-ins, peer catch-ups, new joiner onboarding. For people who work together but don't fully know each other yet." },
  { symbol: '· · ·', name: 'Team',     cat: 'Group Settings',            comingSoon: false, desc: "Standups, retros, all-hands, team socials. Calibrated for voices in a room with different levels of trust with each other." },
  { symbol: '·', name: 'Friends',       cat: 'Social Contexts',           comingSoon: false, desc: "Friend hangs, catch-ups, group trips. For when the conversation isn't about work — but still benefits from a thought to open with." },
  { symbol: '◎', name: 'Solo',          cat: 'Personal Reflection',       comingSoon: true,  desc: "For the moments when you want to sit with a thought on your own. A quiet prompt for the end of the day, or the beginning of one." },
  { symbol: '◇', name: 'Relationship',  cat: 'Personal Connections',      comingSoon: true,  desc: "Partners, close friends, family. The questions that go to more personal territory — for the people you want to actually know." },
];

const TypesSection = () => {
  const { ref, inView } = useReveal();
  return (
    <section ref={ref} className="py-28 px-6 md:px-12" style={{ background: CREAM }}>
      <motion.div
        variants={stagger}
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
        className="max-w-[1200px] mx-auto"
      >
        <motion.div variants={fadeUp}>
          <ELabel>Who it's for</ELabel>
          <h2 style={{ fontFamily: SERIF, fontSize: 'clamp(36px, 4.4vw, 62px)', lineHeight: 1.1, letterSpacing: '-0.035em', color: CHARCOAL, marginBottom: '16px' }}>
            Every context has
            <br />
            its own{' '}
            <em style={{ fontStyle: 'italic', color: ORANGE }}>language.</em>
          </h2>
          <p style={{ fontSize: '18px', fontWeight: 300, color: CHARCOAL_MID, lineHeight: 1.75, maxWidth: '50ch', marginBottom: '56px' }}>
            Choose who you're talking with, and the thought library calibrates — the language, the intimacy, the stakes.
          </p>
        </motion.div>

        <motion.div
          variants={stagger}
          className="grid sm:grid-cols-2 md:grid-cols-3 gap-px rounded-[20px] overflow-hidden"
          style={{ background: RULE, border: `1px solid ${RULE}` }}
        >
          {allTypes.map((t) => (
            <motion.div
              key={t.name}
              variants={fadeUp}
              className="p-11 flex flex-col transition-colors duration-200"
              style={{ background: CREAM, position: 'relative' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.7)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = CREAM; }}
            >
              {t.comingSoon && (
                <div style={{ position: 'absolute', top: '26px', right: '26px', pointerEvents: 'none' }}>
                  <svg width="80" height="32" viewBox="0 0 80 32" style={{ overflow: 'visible' }}>
                    <path id={`arc-${t.name}`} d="M 5,27 Q 40,5 75,27" fill="none" />
                    <text style={{ fontFamily: "'Caveat', cursive", fontSize: '16px' }} fill={CHARCOAL_MUT}>
                      <textPath href={`#arc-${t.name}`} startOffset="50%" textAnchor="middle">Soon</textPath>
                    </text>
                  </svg>
                </div>
              )}
              <div style={{ fontSize: '20px', marginBottom: '14px', color: ORANGE, opacity: 0.8 }}>{t.symbol}</div>
              <h3 style={{ fontFamily: SERIF, fontSize: '26px', letterSpacing: '-0.025em', color: CHARCOAL, marginBottom: '8px' }}>{t.name}</h3>
              <div style={{ fontFamily: MONO, fontSize: '10px', fontWeight: 500, color: ORANGE, marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                {t.cat}
              </div>
              <p style={{ fontSize: '14px', fontWeight: 300, color: CHARCOAL_MID, lineHeight: 1.68 }}>{t.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};

// ── 7. Stats ───────────────────────────────────────────────────────────────────
const StatsSection = () => {
  const { ref, inView } = useReveal();
  const stats = [
    { num: '5', unit: 'min', label: 'Before every meeting.\nThat\'s all it takes.' },
    { num: '3',  unit: '×',  label: 'Levels of depth — from\nlight to transformative.' },
    { num: '6',  unit: '+',  label: 'Conversation types,\none for every context.' },
  ];
  return (
    <section ref={ref} className="py-20 px-6 md:px-12" style={{ background: CREAM }}>
      <motion.div
        variants={stagger}
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
        className="max-w-[1200px] mx-auto"
      >
        <motion.div
          variants={stagger}
          className="grid grid-cols-3 gap-px rounded-[16px] overflow-hidden"
          style={{ background: RULE, border: `1px solid ${RULE}` }}
        >
          {stats.map((s) => (
            <motion.div key={s.num} variants={fadeUp} className="p-11" style={{ background: CREAM }}>
              <div style={{ fontFamily: SERIF, fontSize: '52px', letterSpacing: '-0.04em', lineHeight: 1, color: CHARCOAL, marginBottom: '8px' }}>
                {s.num}<span style={{ color: ORANGE }}>{s.unit}</span>
              </div>
              <div style={{ fontSize: '14px', fontWeight: 300, color: CHARCOAL_MUT, lineHeight: 1.5, whiteSpace: 'pre-line' }}>{s.label}</div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};

// ── 8. Manifesto ───────────────────────────────────────────────────────────────
const ManifestoSection = () => {
  const { ref, inView } = useReveal();

  const paras = [
    {
      style: 'body',
      text: 'Most remote teams are good at working together. Very few are good at knowing each other. Those are not the same thing, and somewhere along the way we stopped noticing the difference.',
    },
    {
      style: 'accent',
      text: 'I built this because I kept sitting in calls with people I genuinely liked and realised I had no idea what was actually going on in their lives — the way I used to when we were in the same room.',
    },
    {
      style: 'body',
      text: 'So I started asking a question at the start of every call. Just one thought, before the work began. Something worth answering. Five minutes to answer it.',
    },
    {
      style: 'body',
      text: 'But something was missing. I was the one choosing the question, which meant I was the one holding all the power. So I started thinking about what it would look like if we both decided — if we could both see the same thought, both say yes or no, and only talk about it once we were both ready. Like Tinder, but for thoughts. And that was when Remote Conversations was born, with Wavelength built straight in.',
    },
    {
      style: 'body',
      text: 'Every part of this is about intentionality. The labels say "We talked about this" instead of "Mark complete" because this is not a task. The Wavelengths are named GENTLE-BREEZE-87 instead of a random string because the room you share with someone should feel like it belongs to you both.',
    },
    {
      style: 'body',
      text: 'Every decision is trying to do the same thing: make it a little easier to be human, before the day takes over.',
    },
    {
      style: 'closing',
      text: 'That is the whole thing.',
    },
  ];

  return (
    <section ref={ref} className="py-32 px-6 md:px-12" style={{ background: CREAM }}>
      <motion.div
        variants={stagger}
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
        className="max-w-[660px] mx-auto"
      >
        <motion.div variants={fadeUp}>
          <div className="flex items-center gap-3 mb-14">
            <div className="w-5 h-px" style={{ background: 'rgba(44,36,23,0.3)' }} />
            <span style={{ fontFamily: MONO, fontSize: '10px', letterSpacing: '0.17em', textTransform: 'uppercase', color: CHARCOAL_MUT }}>
              A note from the creator
            </span>
          </div>
        </motion.div>

        <div className="flex flex-col" style={{ gap: '28px' }}>
          {paras.map((p, i) => (
            <motion.p
              key={i}
              variants={fadeUp}
              style={
                p.style === 'accent'
                  ? {
                      fontFamily: SERIF,
                      fontSize: '20px',
                      lineHeight: 1.65,
                      letterSpacing: '-0.015em',
                      color: CHARCOAL,
                    }
                  : p.style === 'closing'
                  ? {
                      fontFamily: SERIF,
                      fontSize: '19px',
                      lineHeight: 1.6,
                      letterSpacing: '-0.015em',
                      color: CHARCOAL,
                      marginTop: '8px',
                    }
                  : {
                      fontSize: '17px',
                      fontWeight: 300,
                      lineHeight: 1.85,
                      color: CHARCOAL_MID,
                    }
              }
            >
              {p.text}
            </motion.p>
          ))}
        </div>

        <motion.div
          variants={fadeUp}
          className="flex items-center gap-2 mt-10"
          style={{ fontFamily: HAND, fontSize: '22px', color: CHARCOAL_MUT }}
        >
          <span style={{ color: ORANGE }}>—</span>
          Edwin Watson
        </motion.div>
      </motion.div>
    </section>
  );
};

// ── 9. Testimonials ────────────────────────────────────────────────────────────
const testimonials = [
  { quote: "We've done more genuine team building in two weeks of this than in a year of Friday socials.", name: 'Sarah J.', role: 'Head of Engineering, remote-first startup', initials: 'SJ' },
  { quote: "I learned something about my team lead I never would have known. That changed how we work together.", name: 'Marcus T.', role: 'Product Designer, distributed agency', initials: 'MT' },
  { quote: "The Depth questions are where it gets interesting. My team surprised me every single time.", name: 'Aya K.', role: 'Chief of Staff, global SaaS company', initials: 'AK' },
  { quote: "We're in four time zones and for five minutes every Monday we're all in the same room.", name: 'Luca P.', role: 'Engineering Manager, fintech', initials: 'LP' },
  { quote: "The price is a coffee. For what it does to team culture, it should cost ten times more.", name: 'Riya N.', role: 'VP People, tech company', initials: 'RN' },
  { quote: "I used it before a difficult 1-on-1 and it completely changed the energy. We actually talked.", name: 'David K.', role: 'Engineering Manager, Series B startup', initials: 'DK' },
];

const TestimonialsSection = () => {
  const { ref, inView } = useReveal();
  return (
    <section ref={ref} className="py-28 overflow-hidden" style={{ background: CREAM }}>
      <motion.div
        variants={stagger}
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
        className="max-w-[1200px] mx-auto px-6 md:px-12 mb-14"
      >
        <motion.div variants={fadeUp}>
          <ELabel>What people say</ELabel>
          <h2 style={{ fontFamily: SERIF, fontSize: 'clamp(36px, 4.4vw, 62px)', lineHeight: 1.1, letterSpacing: '-0.035em', color: CHARCOAL }}>
            For people who actually
            <br />
            want to know{' '}
            <em style={{ fontStyle: 'italic', color: ORANGE }}>each other.</em>
          </h2>
        </motion.div>
      </motion.div>

      <style>{`
        @keyframes rc-ticker {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        .rc-ticker-track {
          display: flex;
          gap: 20px;
          width: max-content;
          animation: rc-ticker 48s linear infinite;
          padding: 0 10px;
        }
        .rc-ticker-track:hover { animation-play-state: paused; }
      `}</style>

      <div className="rc-ticker-track">
        {[...testimonials, ...testimonials].map((t, i) => (
          <div
            key={i}
            className="rounded-[16px] p-8 flex-shrink-0 flex flex-col justify-between"
            style={{
              width: '340px',
              background: 'rgba(255,255,255,0.6)',
              border: `1px solid ${RULE}`,
              backdropFilter: 'blur(6px)',
            }}
          >
            <p style={{ fontFamily: SERIF, fontSize: '17px', lineHeight: 1.55, letterSpacing: '-0.015em', color: CHARCOAL, marginBottom: '24px' }}>
              "{t.quote}"
            </p>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold text-white flex-shrink-0" style={{ background: 'rgba(255,120,10,0.14)', color: ORANGE }}>
                {t.initials}
              </div>
              <div>
                <div style={{ fontSize: '13px', fontWeight: 500, color: CHARCOAL }}>{t.name}</div>
                <div style={{ fontSize: '12px', fontWeight: 300, color: CHARCOAL_MUT }}>{t.role}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

// ── 10. Closing CTA ────────────────────────────────────────────────────────────
const CTASection = ({ handleCTA, isAuthenticated }) => {
  const { ref, inView } = useReveal();
  const navigate = useNavigate();
  return (
    <section
      ref={ref}
      className="relative py-36 px-6 overflow-hidden"
      style={{ background: `radial-gradient(ellipse 80% 50% at 50% 80%, rgba(255,120,10,0.09) 0%, transparent 65%), ${CREAM}` }}
    >
      <motion.div
        variants={stagger}
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
        className="max-w-[1200px] mx-auto text-center"
      >
        <motion.h2
          variants={fadeUp}
          style={{
            fontFamily: SERIF,
            fontSize: 'clamp(48px, 7vw, 88px)',
            lineHeight: 1.05,
            letterSpacing: '-0.045em',
            color: CHARCOAL,
            marginBottom: '28px',
          }}
        >
          Ready when
          <br />
          you{' '}
          <em style={{ fontStyle: 'italic', color: ORANGE }}>are.</em>
        </motion.h2>

        <motion.p
          variants={fadeUp}
          style={{ fontSize: '18px', fontWeight: 300, color: CHARCOAL_MID, maxWidth: '44ch', margin: '0 auto 52px', lineHeight: 1.75 }}
        >
          No sign-up required to start. Open the app, a thought is already waiting. Five minutes.
          Then the meeting begins.
        </motion.p>

        <motion.div variants={fadeUp} className="flex justify-center">
          <button
            onClick={() => navigate('/')}
            style={{ fontFamily: HAND, fontSize: '22px', color: CHARCOAL_MUT, cursor: 'pointer', background: 'none', border: 'none', padding: '0' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = CHARCOAL; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = CHARCOAL_MUT; }}
            data-testid="landing-scroll-top"
          >
            Open the app to try it ↑
          </button>
        </motion.div>
      </motion.div>
    </section>
  );
};

// ── 11. Footer ─────────────────────────────────────────────────────────────────
const LandingFooter = () => {
  const navigate = useNavigate();
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
  <footer style={{ background: CREAM, borderTop: `1px solid ${RULE}` }}>
    {/* Main footer: logo + tagline left, links right */}
    <div
      className="max-w-[1200px] mx-auto px-6 md:px-12 py-12 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-10 items-start"
    >
      {/* Left */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: ORANGE }} />
          <span style={{ fontSize: '14px', fontWeight: 500, letterSpacing: '-0.01em', color: CHARCOAL }}>
            remote<span style={{ color: ORANGE }}>conversations</span>
          </span>
        </div>
        <p style={{ fontSize: '13px', fontWeight: 300, color: CHARCOAL_MUT, maxWidth: '36ch', lineHeight: 1.6 }}>
          One thought. Before every meeting. For remote teams who want to actually know each other.
        </p>
      </div>

      {/* Right: links */}
      <div className="flex flex-wrap gap-7 items-center">
        {[
          { label: 'How it works', action: () => scrollTo('how-it-works') },
          { label: 'Levels',       action: () => scrollTo('levels') },
          { label: 'Wavelength',   action: () => scrollTo('wavelength') },
          { label: 'Open app',     action: () => navigate('/') },
        ].map(({ label, action }) => (
          <button
            key={label}
            onClick={action}
            className="transition-colors duration-200"
            style={{ fontSize: '13px', color: CHARCOAL_MUT }}
            onMouseEnter={(e) => { e.currentTarget.style.color = CHARCOAL; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = CHARCOAL_MUT; }}
          >
            {label}
          </button>
        ))}
      </div>
    </div>

    {/* Bottom bar */}
    <div
      className="max-w-[1200px] mx-auto px-6 md:px-12 py-5 flex flex-col sm:flex-row items-center justify-between gap-2"
      style={{ borderTop: `1px solid ${RULE}` }}
    >
      <span style={{ fontFamily: MONO, fontSize: '10px', letterSpacing: '0.08em', textTransform: 'uppercase', color: CHARCOAL_MUT }}>
        © 2026 Remote Conversations · Built with care
      </span>
      <span style={{ fontFamily: MONO, fontSize: '10px', letterSpacing: '0.08em', textTransform: 'uppercase', color: CHARCOAL_MUT }}>
        Think together.
      </span>
    </div>

    {/* Handwritten attribution */}
    <div
      className="max-w-[1200px] mx-auto px-6 md:px-12 py-5 text-center"
      style={{ borderTop: `1px solid ${RULE}` }}
    >
      <span style={{ fontFamily: HAND, fontSize: '17px', color: CHARCOAL_MUT }}>
        Made with 🧡 for remote teams around the world 🌏
      </span>
    </div>
  </footer>
  );
};

// ── Main export ────────────────────────────────────────────────────────────────
const LandingSection = () => {
  const modal = useModal();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const handleCTA = () => {
    if (!isAuthenticated) modal.openAuth();
    else navigate('/');
  };

  return (
    <div id="landing-section" data-testid="landing-section">
      <TransitionSection />
      <Rule />
      <WhySection />
      <Rule />
      <HowItWorksSection handleCTA={handleCTA} />
      <Rule />
      <DepthSection />
      <Rule />
      <WavelengthSection />
      <TypesSection />
      <Rule />
      <ManifestoSection />
      <Rule />
      <CTASection handleCTA={handleCTA} isAuthenticated={isAuthenticated} />
      <LandingFooter />
    </div>
  );
};

export default LandingSection;
